// The invariant library: small, named checks a game lists once and every test runs after every step.
// Each check returns nothing when the world is sane, or a message saying what broke. Messages are stable
// text ("hero below ground (y -1 < 0)") so tests can match them, and each check is cheap enough to run
// every step. Prove each check can fail with a planted defect (the spike's tests/oracles.spec.ts).

export type InvariantResult = string | readonly string[] | null | undefined | false | void;
export type Invariant = () => InvariantResult;

export interface XZ {
  x: number;
  z: number;
}

export interface XYZ extends XZ {
  y: number;
}

/** Axis-aligned box, min/max per axis. */
export interface Box3 {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  z0: number;
  z1: number;
}

const r3 = (v: number): number => Math.round(v * 1000) / 1000;

/** Run every check; the failures, in order. */
export function checkInvariants(checks: readonly Invariant[]): string[] {
  const out: string[] = [];
  for (const c of checks) {
    let r: InvariantResult;
    try {
      r = c();
    } catch (e) {
      r = `invariant threw: ${e instanceof Error ? e.message : String(e)}`;
    }
    if (!r) continue;
    if (typeof r === "string") out.push(r);
    else out.push(...r);
  }
  return out;
}

/** Every number finite. Message: "non-finite <label> state". */
export function finite(label: string, values: () => readonly number[]): Invariant {
  return () => (values().every(Number.isFinite) ? null : `non-finite ${label} state`);
}

/** |x| ≤ hx and |z| ≤ hz (centred bounds) or inside min/max. Message: "<label> out of bounds (x, z)". */
export function inBounds(label: string, pos: () => XZ, bounds: { hx: number; hz: number } | { min: XZ; max: XZ }): Invariant {
  return () => {
    const p = pos();
    const out =
      "hx" in bounds
        ? Math.abs(p.x) > bounds.hx || Math.abs(p.z) > bounds.hz
        : p.x < bounds.min.x || p.x > bounds.max.x || p.z < bounds.min.z || p.z > bounds.max.z;
    return out ? `${label} out of bounds (${r3(p.x)}, ${r3(p.z)})` : null;
  };
}

/**
 * y ≥ ground(x, z) + clearance. A small negative clearance tolerates contact noise (a body: -0.02); a positive
 * one keeps something above the ground (a camera: 0.1). Message: "<label> below ground (y a < g)", or just
 * "<label> below ground" with `detail: false`.
 */
export function notBelowGround(label: string, pos: () => XYZ, ground: (x: number, z: number) => number, o: { clearance?: number; detail?: boolean } = {}): Invariant {
  const clearance = o.clearance ?? -0.02;
  return () => {
    const p = pos();
    const g = ground(p.x, p.z);
    if (!(p.y < g + clearance)) return null;
    return o.detail === false ? `${label} below ground` : `${label} below ground (y ${r3(p.y)} < ${r3(g)})`;
  };
}

/** A point strictly inside any box fails. Message: "<label> inside a solid". */
export function outsideBoxes(label: string, pos: () => XYZ, boxes: () => Iterable<Box3>): Invariant {
  return () => {
    const p = pos();
    for (const b of boxes()) if (p.x > b.x0 && p.x < b.x1 && p.z > b.z0 && p.z < b.z1 && p.y > b.y0 && p.y < b.y1) return `${label} inside a solid`;
    return null;
  };
}

/** Any captured page error fails. Message: "<n> page error(s)". */
export function noErrors(errors: readonly string[]): Invariant {
  return () => (errors.length ? `${errors.length} page error(s)` : null);
}

/** Run the checks only while `cond()` holds (e.g. camera checks off in a fixed overview). */
export function when(cond: () => boolean, ...checks: Invariant[]): Invariant {
  return () => (cond() ? checkInvariants(checks) : null);
}

/** A named custom check: `test()` true means broken. */
export function custom(message: string | (() => string), broken: () => boolean): Invariant {
  return () => (broken() ? (typeof message === "string" ? message : message()) : null);
}

/**
 * Every number anywhere in a state object is finite: walks objects and arrays (strings, booleans and null are
 * skipped). Message: "non-finite <label> at <path>" for the first bad number, e.g. "non-finite state at box.y".
 */
export function finiteDeep(label: string, get: () => unknown, o: { maxDepth?: number } = {}): Invariant {
  const maxDepth = o.maxDepth ?? 8;
  const walk = (v: unknown, path: string, depth: number): string | null => {
    if (typeof v === "number") return Number.isFinite(v) ? null : path || "(root)";
    if (v === null || typeof v !== "object" || depth > maxDepth) return null;
    const entries: [string, unknown][] = Array.isArray(v) ? v.map((x, i) => [String(i), x]) : Object.entries(v);
    for (const [k, x] of entries) {
      const bad = walk(x, path ? `${path}.${k}` : k, depth + 1);
      if (bad) return bad;
    }
    return null;
  };
  return () => {
    const bad = walk(get(), "", 0);
    return bad ? `non-finite ${label} at ${bad}` : null;
  };
}

/** What `advancing` reads: sim time, the real time handed to the sim so far, and whether a stop is meant. */
export interface AdvancingSource {
  /** Sim time (any unit that grows with every step: seconds, the step index). */
  time(): number;
  /** Real milliseconds the game has been driven so far (frames plus deterministic advances), never decreasing. */
  drivenMs(): number;
  /** True while the game means to stand still though driven (a deliberate freeze). Optional: `trackDriven` already
   *  leaves paused frames out of the driven time. */
  paused?(): boolean;
}

/**
 * Sim time moves while the game is driven and not paused. Fails when more than `maxStallMs` of real time has been
 * handed to the sim since its time last moved, outside a pause: a time scale or hit-stop stuck at zero, a clock
 * that stopped draining. Stateful (it remembers the last move), so build one per watch. The default, 1000 ms, is
 * longer than any hit-stop or slow-motion gap between steps. Message: "<label> time stuck (<n> ms driven, no step)".
 */
export function advancing(label: string, src: AdvancingSource, o: { maxStallMs?: number } = {}): Invariant {
  const maxStall = o.maxStallMs ?? 1000;
  let lastTime = Number.NaN;
  let drivenAtLast = 0;
  return () => {
    const t = src.time();
    const d = src.drivenMs();
    if (src.paused?.() || t !== lastTime) {
      lastTime = t;
      drivenAtLast = d;
      return null;
    }
    const stalled = d - drivenAtLast;
    return stalled > maxStall ? `${label} time stuck (${Math.round(stalled)} ms driven, no step)` : null;
  };
}

/** What `stuck` reads at each check. */
export interface StuckSource {
  /** Sim time (s). */
  time(): number;
  /** Where the thing is. */
  pos(): XYZ;
  /** Whether input is asking it to move now. */
  wantsToMove(): boolean;
  /** Whether something meant holds it still (pressing into a wall, a cutscene, a mount). Optional. */
  held?(): boolean;
}

/**
 * Stuck: input has asked to move for `seconds` of sim time without the thing getting `minProgress` metres from where
 * it was when the asking began, and nothing meant held it (the two-signal rule: low progress while input is held, so
 * stopping on purpose never counts). Stateful, so build one per watch. Defaults: 2 s, 0.1 m.
 * Message: "<label> stuck (input held <seconds> s, no progress)".
 */
export function stuck(label: string, src: StuckSource, o: { seconds?: number; minProgress?: number } = {}): Invariant {
  const seconds = o.seconds ?? 2;
  const minProgress = o.minProgress ?? 0.1;
  let since = Number.NaN;
  let from: XYZ = { x: 0, y: 0, z: 0 };
  return () => {
    const t = src.time();
    const p = src.pos();
    const asking = src.wantsToMove() && src.held?.() !== true;
    if (!asking || !(t >= since) || Math.hypot(p.x - from.x, p.y - from.y, p.z - from.z) >= minProgress) {
      // not asking, a clock that went back (a restore), or progress made: the window starts again here
      since = asking ? t : Number.NaN;
      from = { x: p.x, y: p.y, z: p.z };
      return null;
    }
    return t - since >= seconds ? `${label} stuck (input held ${seconds} s, no progress)` : null;
  };
}
